import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators'
import { combineLatest, Observable, of } from 'rxjs'
import { ActionFailed, ActionSkipped, isSomething, Toaster } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import {
  AddAutomation,
  AddAutomationComplete,
  AddAutomationFailed,
  ArchiveAutomation,
  ArchiveAutomationComplete,
  DeleteAutomation,
  DeleteAutomationComplete,
  LoadAutomations,
  LoadAutomationsComplete,
  PauseAutomation,
  PauseAutomationComplete,
  RecreateAutomation,
  UnArchiveAutomation,
  UnArchiveAutomationComplete,
  UnPauseAutomation,
  UnPauseAutomationComplete,
  UpdateAutomations,
  UpdateAutomationsComplete,
} from './automation.actions'
import { AutomationResponse, CreateAutomationPayload } from './automation.model'
import { AutomationService } from './automation.service'
import * as lodash from 'lodash'

@Injectable()
export class AutomationEffects {
  @Effect()
  loadAutomations$ = this.actions$.pipe(
    ofType(LoadAutomations),
    switchMap(({ services }) =>
      this.automationService.all(1000, 0, services).pipe(
        map((automations) => LoadAutomationsComplete({ automations: lodash.uniqBy(automations, (t) => t.pkey) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteAutomations$ = this.actions$.pipe(
    ofType(DeleteAutomation),
    concatMap(({ ids, silent }) =>
      this.automationService.deleteBatch(ids).pipe(
        tap(() => !silent && this.toast.success($localize`Automation deleted successfully.`)),
        map(() => DeleteAutomationComplete({ ids })),
        catchError((error) => {
          if (lodash.startsWith(error.message, `Unexpected token '<'`)) {
            this.toast.info(
              $localize`Request accepted. Trigger deletion is in progress. Please check back in a few minutes.`
            )
            return of(ActionSkipped())
          }
          return of(ActionFailed({ error }))
        })
      )
    )
  )

  @Effect()
  pauseAutomation$ = this.actions$.pipe(
    ofType(PauseAutomation),
    concatMap(({ ids, silent }) =>
      this.automationService.pause(ids).pipe(
        tap(() => !silent && this.toast.success($localize`Automation updated successfully. `)),
        map(() => PauseAutomationComplete({ changes: R.map((id) => ({ id, changes: { status: 0 } }), ids) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  resumeAutomation$ = this.actions$.pipe(
    ofType(UnPauseAutomation),
    concatMap(({ ids, silent }) =>
      this.automationService.resume(ids).pipe(
        tap(() => !silent && this.toast.success($localize`Automation updated successfully. `)),
        map(() => UnPauseAutomationComplete({ changes: R.map((id) => ({ id, changes: { status: 1 } }), ids) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveAutomation$ = this.actions$.pipe(
    ofType(ArchiveAutomation),
    switchMap(({ ids }) =>
      this.automationService.archive(ids).pipe(
        tap(() => this.toast.success($localize`Automation archived successfully.`)),
        map(() => ArchiveAutomationComplete({ changes: R.map((id) => ({ id, changes: { archived: 1 } }), ids) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unArchiveAutomation$ = this.actions$.pipe(
    ofType(UnArchiveAutomation),
    switchMap(({ ids }) =>
      this.automationService.unArchive(ids).pipe(
        tap(() => this.toast.success($localize`Automation unarchived successfully.`)),
        map(() => UnArchiveAutomationComplete({ changes: R.map((id) => ({ id, changes: { archived: 0 } }), ids) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  // One Save in the Autopilot Wizard makes every row on the panel dispatch its own
  // AddAutomation in the same tick. switchMap would cancel every request but the last
  // one, leaving those rows with no per-rental triggers while their narrowing update
  // still lands (TV4-6238). Keep this concatMap.
  @Effect()
  addAutomation$ = this.actions$.pipe(
    ofType(AddAutomation),
    concatMap(({ ids, payload, silent }) =>
      this.createAutomation(payload).pipe(
        // Drop the replaced automations only once their replacements exist. Deleting
        // first loses the automation outright when the create fails (TV4-6238).
        concatMap((automations) =>
          this.removeReplaced(ids).pipe(map((cleanupError) => ({ automations, cleanupError })))
        ),
        tap(() => !silent && this.toast.success($localize`Automation created successfully.`)),
        switchMap(({ automations, cleanupError }) => {
          // The new automations reach the store even when the cleanup failed. Dropping
          // them would hide live triggers and let the next save create them twice.
          const actions: any[] = [AddAutomationComplete({ automations })]
          if (isSomething(ids)) {
            actions.push(
              cleanupError ? ActionFailed({ error: cleanupError }) : DeleteAutomationComplete({ ids: ids || [] })
            )
          }
          return actions
        }),
        catchError((error) => {
          if (lodash.startsWith(error.message, `Unexpected token '<'`)) {
            this.toast.info(
              $localize`Request accepted. Trigger creation is in progress. Please check back in a few minutes.`
            )
            return of(AddAutomationFailed(), ActionSkipped())
          }
          return of(AddAutomationFailed(), ActionFailed({ error }))
        })
      )
    )
  )

  @Effect()
  recreateAutomation$ = this.actions$.pipe(
    ofType(RecreateAutomation),
    concatMap(({ ids, payload }) => {
      return combineLatest([this.createAutomation(payload), this.automationService.deleteBatch(ids || [])]).pipe(
        tap(() => this.toast.success($localize`Automation updated successfully.`)),
        switchMap(([automations]) => [
          AddAutomationComplete({ automations }),
          DeleteAutomationComplete({ ids: ids || [] }),
        ]),
        catchError((error) => {
          if (lodash.startsWith(error.message, `Unexpected token '<'`)) {
            this.toast.info(
              $localize`Request accepted. Trigger creation is in progress. Please check back in a few minutes.`
            )
            return of(ActionSkipped())
          }
          return of(ActionFailed({ error }))
        })
      )
    })
  )

  @Effect()
  updateAutomation$ = this.actions$.pipe(
    ofType(UpdateAutomations),
    concatMap(({ items, silent }) => {
      return this.automationService.updateMany(items).pipe(
        tap(() => !silent && this.toast.success($localize`Automation updated successfully. `)),
        map((automations) =>
          UpdateAutomationsComplete({ updates: automations.map((t) => ({ id: t.pkey, changes: t })) })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  constructor(private actions$: Actions, private automationService: AutomationService, private toast: Toaster) {}

  // Removes the automations a create has just replaced. Returns the error instead of
  // throwing, so a failed cleanup never discards the create that already succeeded.
  private removeReplaced(ids?: string[]): Observable<any> {
    if (!isSomething(ids)) {
      return of(null)
    }

    return this.automationService.deleteBatch(ids || []).pipe(
      map(() => null),
      catchError((error) => of(error))
    )
  }

  private createAutomation(payload: CreateAutomationPayload): Observable<AutomationResponse[]> {
    const batch = isSomething(payload.channels) || isSomething(payload.rentals)
    if (batch) {
      return this.automationService.createMany(payload)
    } else {
      return this.automationService.create(payload).pipe(map((item) => [item]))
    }
  }
}
